import React from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { useListEdition } from "@planzone/view-models";
import { VisibilityWatcher } from "@common/visibilityWatcher";
import { Calendar } from "@common/calendar";
import { ActivityWatcher } from "@common/activityWatcher";
import { Props } from "./types";
import styles from "./styles.scss";

function DateForm({ project }: Props) {
  const { t } = useTranslation();
  const { isRequestPending, editList } = useListEdition();
  const [startDate, setStartDate] = React.useState(project.rootList.startDate);
  const [endDate, setEndDate] = React.useState(project.rootList.endDate);
  const hasChanged =
    startDate !== project.rootList.startDate ||
    endDate !== project.rootList.endDate;
  const start = new Date(startDate || new Date());
  const end = new Date(endDate || new Date());
  const { rootList } = project;
  // Same bounds as the project row of the Gantt details panel: editing stays
  // anchored to the project's timezone (what the backend compares day
  // boundaries against) and keeps the stored hour/minute. Bounds are
  // hour-precise (e.g. an HOUR-granularity constraint) while the Calendar only
  // lets the user pick a day, so the picked instant is clamped to the bound's
  // exact instant, otherwise it can land out of bounds and be rejected.
  const zone = project.timeZone || DateTime.local().zoneName;
  const toDateTime = (iso?: string | null) => {
    if (!iso) return undefined;
    const dt = DateTime.fromISO(iso, { zone: "utc" }).setZone(zone);
    return dt.isValid ? dt : undefined;
  };
  const toCalendarDate = (dt?: DateTime) =>
    dt ? { year: dt.year, month: dt.month - 1, day: dt.day } : undefined;
  const editStart = toDateTime(startDate);
  const editEnd = toDateTime(endDate);
  const startMaxBound = toDateTime(
    rootList.maxStartDate || endDate || rootList.maxEndDate,
  );
  const endMinBound = toDateTime(
    rootList.minEndDate || startDate || rootList.minStartDate,
  );
  const pick = (
    date: { year: number; month: number; day: number },
    current?: DateTime,
    min?: DateTime,
    max?: DateTime,
  ) => {
    const picked = DateTime.fromObject(
      {
        year: date.year,
        month: date.month + 1,
        day: date.day,
        hour: current?.hour ?? 0,
        minute: current?.minute ?? 0,
      },
      { zone },
    );
    const clamped =
      min && picked < min ? min : max && picked > max ? max : picked;
    return clamped.toUTC().toISO() ?? "";
  };
  const isSameInstant = (iso: string, stored?: string) =>
    !!stored &&
    DateTime.fromISO(iso).toMillis() === DateTime.fromISO(stored).toMillis();

  React.useEffect(() => {
    setStartDate(project.rootList.startDate);
  }, [project.rootList.startDate]);

  React.useEffect(() => {
    setEndDate(project.rootList.endDate);
  }, [project.rootList.endDate]);

  return (
    <ActivityWatcher
      className={styles.dateForm}
      blur={() =>
        !isRequestPending &&
        hasChanged &&
        editList({
          list: project.rootList,
          nextList: { ...project.rootList, startDate, endDate },
        })
      }
    >
      <VisibilityWatcher className={styles.group}>
        {(isVisible, show, hide) => (
          <React.Fragment>
            {isVisible && (
              <div className={styles.calendar}>
                <Calendar
                  date={
                    toCalendarDate(editStart) ?? {
                      year: start.getFullYear(),
                      month: start.getMonth(),
                      day: start.getDate(),
                    }
                  }
                  maxDate={toCalendarDate(startMaxBound)}
                  clear={() =>
                    editList({
                      list: project.rootList,
                      nextList: { ...project.rootList, startDate: "" },
                    })
                  }
                  editDate={(date) => {
                    const nextDate = pick(
                      date,
                      editStart,
                      undefined,
                      startMaxBound,
                    );

                    editList({
                      list: project.rootList,
                      nextList: {
                        ...project.rootList,
                        startDate: isSameInstant(
                          nextDate,
                          project.rootList.startDate,
                        )
                          ? ""
                          : nextDate,
                      },
                    });
                  }}
                />
              </div>
            )}
            <h3 className={styles.titleStart}>
              {t("projectDetails.startDateTitle")}
            </h3>
            <label className={styles.wrapper}>
              <input
                className={styles.input}
                value={
                  startDate ? new Date(startDate).toLocaleDateString() : ""
                }
                onFocus={show}
                readOnly
              />
            </label>
          </React.Fragment>
        )}
      </VisibilityWatcher>
      <VisibilityWatcher className={styles.group}>
        {(isVisible, show, hide) => (
          <React.Fragment>
            {isVisible && (
              <div className={styles.calendar}>
                <Calendar
                  date={
                    toCalendarDate(editEnd) ?? {
                      year: end.getFullYear(),
                      month: end.getMonth(),
                      day: end.getDate(),
                    }
                  }
                  minDate={toCalendarDate(endMinBound)}
                  clear={() =>
                    editList({
                      list: project.rootList,
                      nextList: { ...project.rootList, endDate: "" },
                    })
                  }
                  editDate={(date) => {
                    const nextDate = pick(date, editEnd, endMinBound);

                    editList({
                      list: project.rootList,
                      nextList: {
                        ...project.rootList,
                        endDate: isSameInstant(
                          nextDate,
                          project.rootList.endDate,
                        )
                          ? ""
                          : nextDate,
                      },
                    });
                  }}
                />
              </div>
            )}
            <h3 className={styles.titleEnd}>
              {t("projectDetails.endDateTitle")}
            </h3>
            <label className={styles.wrapper}>
              <input
                className={styles.input}
                value={endDate ? new Date(endDate).toLocaleDateString() : ""}
                onFocus={show}
                readOnly
              />
            </label>
          </React.Fragment>
        )}
      </VisibilityWatcher>
    </ActivityWatcher>
  );
}

export { DateForm };
